import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { INVITE_PROFESSIONALS_TO_JOBS, inviteProfessionalsToJobs, SHOW_MESSAGE } from '~/actions';
import { clearSuccess } from '~/actions/success';
import { createSuccessSelector } from '~/api/selectors';
import { Box, Button, Center, CustomSpinner, Row, Text } from '~/commonComponents';
import Portal from '~/commonComponents/Portal';
import { IEligibleJob } from '~/domain/EligibleJobs';
import browserHistory from '~/browserHistory';
import { useLoading } from '~/hooks';
import ClosePopupIcon from '~/images/ClosePopupIcon.svg';
import defaultProfile from '~/images/HY_tooth.png';
import { IUser } from '~/models';
import { routes } from '~/routes';
import { Colors } from '~/themes/colors';
import { EligibleJobCard } from './EligibleJobCard';

interface IRatingSentSuccessPopupProps {
  eligibleJobs: IEligibleJob[];
  professional: IUser;
  officeId: string;
  onClose: () => void;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  onSubmitFeedback: () => void;
}

const inviteAction = [INVITE_PROFESSIONALS_TO_JOBS];
const successSelector = createSuccessSelector(inviteAction);

export const RatingSentSuccessPopup = ({
  eligibleJobs,
  professional,
  officeId,
  onClose,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  onSubmitFeedback,
}: IRatingSentSuccessPopupProps) => {
  const dispatch = useDispatch();
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const invitedSuccessfully = useSelector((state) => successSelector(state));
  const { isLoading: isInviting } = useLoading(inviteAction);

  const handleJobSelect = useCallback((jobId: string) => {
    setSelectedJobIds((prev) => {
      if (prev.includes(jobId)) {
        return prev.filter((id) => id !== jobId);
      }
      return [...prev, jobId];
    });
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (scrolledToBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handleInviteClick = () => {
    if (selectedJobIds.length > 0) {
      onSubmitFeedback();
      dispatch(
        inviteProfessionalsToJobs({
          professionalIds: [professional.id],
          jobIds: selectedJobIds,
          officeId,
        }),
      );
    }
  };

  const handlePopupClose = useCallback(() => {
    setSelectedJobIds([]);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (invitedSuccessfully) {
      handlePopupClose();
      dispatch(clearSuccess(INVITE_PROFESSIONALS_TO_JOBS));
      dispatch({
        type: SHOW_MESSAGE,
        payload: {
          isSuccess: true,
          popupProps: {
            rightButtonText: 'Close',
          },
          route: '/',
          title: 'Request(s) Successfully Sent!',
          message: (
            <>
              <b>
                {professional?.first_name} {professional?.last_name}
              </b>{' '}
              has been invited to the selected shift(s). Once they accept at the listed rate,{' '}
              {professional?.first_name} will be confirmed!
            </>
          ),
        },
      });
    }
  }, [invitedSuccessfully, professional, dispatch, handlePopupClose]);

  const onCardPress = (jobId: string) => {
    browserHistory.push(routes.jobDetails.replace(':jobId', jobId));
  };

  return (
    <Portal>
      <Box
        className="modal"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
        }}
      >
        <Box
          className="modal_content"
          style={{
            position: 'relative',
            padding: '40px 30px',
            maxWidth: 700,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <Box style={{ position: 'absolute', top: 20, right: 20, cursor: 'pointer' }}>
            <img alt="close-popup-icon" src={ClosePopupIcon} onClick={onClose} />
          </Box>

          <Text fontSize={18} fontWeight="bold">
            Rating Sent Successfully!
          </Text>

          <Box style={{ marginTop: 12, marginBottom: 16, textAlign: 'center' }}>
            {professional?.profile_image ? (
              <img
                src={professional.profile_image}
                style={{
                  width: 68,
                  height: 68,
                  borderRadius: 68,
                  objectFit: 'cover',
                }}
                alt="profile_image"
              />
            ) : (
              <Center
                backgroundColor={Colors.secondary_500}
                borderRadius={68}
                width={68}
                height={68}
              >
                <img src={defaultProfile} alt="profile_image" width="40px" height="40px" />
              </Center>
            )}
          </Box>

          <Text color="neutral_500" textAlign="center">
            Sounds like{' '}
            <Text bold color="neutral_500">
              {professional?.first_name?.length > 20
                ? professional?.first_name?.slice(0, 20) + '...'
                : professional?.first_name}
            </Text>{' '}
            was a great fit.
            <br />
            Would you like to invite them to upcoming shifts?
          </Text>

          <Box
            ref={scrollContainerRef}
            flex={1}
            marginBottom={24}
            marginTop={16}
            overflowY="auto"
            overflowX="hidden"
            maxHeight="400px"
            scrollbarWidth="thin"
          >
            {isLoading ? (
              <Center
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: 40,
                }}
              >
                <CustomSpinner isLoading />
              </Center>
            ) : (
              <Box marginHorizontal={8} marginTop={4} gap={10} paddingBottom={8}>
                {eligibleJobs.map((job) => (
                  <EligibleJobCard
                    key={job.id}
                    job={job}
                    isSelected={selectedJobIds.includes(job.id)}
                    onSelect={handleJobSelect}
                    disabled={isInviting}
                    onCardPress={onCardPress}
                  />
                ))}

                {isFetchingNextPage && (
                  <Center
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      padding: 16,
                    }}
                  >
                    <CustomSpinner isLoading size={24} />
                  </Center>
                )}
              </Box>
            )}
          </Box>

          <Row gap={16} justifyContent="center" marginTop={16}>
            <Button
              style={{
                alignSelf: 'center',
                width: 186,
                margin: 0,
                backgroundColor: Colors.secondary_500,
              }}
              onClick={onClose}
              text="No Thanks"
              disabled={isInviting}
            />

            <Button
              text={
                selectedJobIds.length > 0
                  ? `Invite for ${selectedJobIds.length} Shift${selectedJobIds.length !== 1 ? 's' : ''}`
                  : 'Invite'
              }
              onClick={handleInviteClick}
              disabled={selectedJobIds.length === 0}
              style={{
                alignSelf: 'center',
                width: 186,
                margin: 0,
                backgroundColor:
                  selectedJobIds.length > 0 ? Colors.primary_500 : Colors.primary_200,
              }}
              isLoading={isInviting}
            />
          </Row>
        </Box>
      </Box>
    </Portal>
  );
};
